import { Container, DefaultPageLayout } from 'routes/components/Layout'
import { LINKS } from 'routes/constants'
import { BreadcrumbsNavigation } from 'routes/components/Shared'
import { BestSupport } from 'routes/components/Features/BestSupport'
import {
  getSolutionsBreadcrumbs,
  PageType,
  SolutionsHeroType,
} from 'routes/Solutions/helpers'
import { SolutionHeadMetatag } from 'routes/Solutions/components/SolutionHeadMetatag'
import { SolutionHero } from 'routes/Solutions/components/SolutionHero'
import { SolutionHIL } from 'routes/Solutions/components/SolutionHIL'
import { SolutionHowItWorks } from 'routes/Solutions/components/SolutionHowItWorks'
import { SolutionCompanies } from 'routes/Solutions/components/SolutionCompanies'
import { SolutionInfrastructure } from 'routes/Solutions/components/SolutionInfrastructure'
import AppsGrid from '../components/SolutionHIL/components/AppsGrid'
import Example from '../components/SolutionHIL/components/Example'
import MonitorStatus from '../components/SolutionHIL/components/MonitorStatus'
import importFromExternalAppMob1x from './import-from-external-app-mob@1x.avif'
import importFromExternalAppMob2x from './import-from-external-app-mob@2x.avif'
import importFromExternalAppDesk1x from './import-from-external-app-desk@1x.avif'
import importFromExternalAppDesk2x from './import-from-external-app-desk@2x.avif'
import availableData1x from './available-data@1x.png'
import availableData2x from './available-data@2x.png'
import fieldMappings1x from 'routes/Solutions/components/SolutionHIL/components/FieldMappings/field-mappings@1x.avif'
import fieldMappings2x from 'routes/Solutions/components/SolutionHIL/components/FieldMappings/field-mappings@2x.avif'
import classes from '../solutions.module.scss'

export const pageData: PageType = {
  title: 'Import Data from External App',
  href: LINKS.SOLUTIONS.IMPORT_FROM_EXTERNAL_APP,
  imageSrc: `${importFromExternalAppDesk1x.src}`,
  srcSet: `${importFromExternalAppDesk1x.src} 1x, ${importFromExternalAppDesk2x.src} 2x`,
  description:
    'Let your users navigate data in external app: files, people, companies, etc.',
  large: true,
}

const pageHeroData: SolutionsHeroType = {
  title: 'Import user-selected data from external app',
  subtitle:
    'Let your users navigate data in external app: files, people, companies, etc. Then import the selected data into your app and keep it in sync.',
  imageWidth: '308',
  imageHeight: '781',
  mobile1x: importFromExternalAppMob1x,
  mobile2x: importFromExternalAppMob2x,
  desktop1x: importFromExternalAppDesk1x,
  desktop2x: importFromExternalAppDesk2x,
}

export default function ImportFromExternalApp() {
  const breadcrumbs = getSolutionsBreadcrumbs(pageData.title)

  return (
    <DefaultPageLayout className={classes.page}>
      <SolutionHeadMetatag pageData={pageData} />
      <Container>
        <BreadcrumbsNavigation
          breadcrumbs={breadcrumbs}
          className={classes.breadcrumbs}
        />
      </Container>
      <Container grid>
        <SolutionHero {...pageHeroData} />
        <SolutionHIL
          fiveItems={true}
          titles={[
            { text: 'Select another SaaS to import data from' },
            { text: 'Navigate the available data' },
            { text: 'Configure field mappings', optional: true },
            {
              text: 'See data appear in your app and being updated in real-time',
            },
            {
              text: 'Monitor imports and react to errors',
              h64: true,
              optional: true,
            },
          ]}
        >
          <AppsGrid isWide />
          <img
            srcSet={`${availableData1x.src} 1x, ${availableData2x.src} 2x`}
            src={availableData1x.src}
            alt='Navigate the available data'
            width={398}
            height={268}
            loading='lazy'
          />
          <img
            srcSet={`${fieldMappings1x.src} 1x, ${fieldMappings2x.src} 2x`}
            src={fieldMappings1x.src}
            alt='Configure field mappings'
            width={522}
            height={314}
            loading='lazy'
          />
          <Example />
          <MonitorStatus />
        </SolutionHIL>
        <SolutionHowItWorks
          guideLink='https://docs.integration.app/docs/import-user-selected-data-collections#/'
          steps={[
            'Select apps you want your customers to import data from',
            'Configure data sources',
            'Configure actions to fetch user-specific filters (folders, lists, etc)',
            'Configure an action to import data',
            "Connect your customer's account in an external app",
            'Run the import action via API or SDK and save data in your database',
          ]}
        />
        <SolutionCompanies />
        <SolutionInfrastructure />
        <BestSupport
          isSolutionsSection
          subtitle={
            <>
              Integrations are hard and messy. <br /> Our success team will help
              you every step of the way.
            </>
          }
          className={classes.support}
        />
      </Container>
    </DefaultPageLayout>
  )
}
